import { createFileRoute } from '@tanstack/react-router'
import { FabricDataModelPage, type FabricDataModelParams } from '@bloo-team/product-fabric'

/**
 * Composer shim (S5.0, docs/modularization.md §Route mounting): the route
 * infrastructure — path, search-param validation — stays in the app's tree;
 * the surface lives in @bloo-team/product-fabric. `?stream=` selects a stream
 * (the right pane shows its fields); `?q=` is the field query — cross-stream
 * when no stream is selected, the name filter when one is
 * (docs/data-model-requirements.md §The page). `?chip=` is the stream
 * list's filter chip — only the five chip keys pass; Datafabric · Overview
 * links `?chip=missing` and `?chip=empty`.
 */
function DataModelRoute() {
  const { clusterId, scopeId } = Route.useParams()
  const { stream, q, chip } = Route.useSearch()
  return <FabricDataModelPage clusterId={clusterId} scopeId={scopeId} stream={stream} q={q} chip={chip} />
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() !== '' ? v : undefined)
type Chip = NonNullable<FabricDataModelParams['chip']>
/** Exhaustive by type: a chip key added to the page's `StreamChip` fails to compile here until listed. */
const CHIP_KEYS: Record<Chip, true> = { stock: true, custom: true, 'has-data': true, empty: true, missing: true }
const chipKey = (v: unknown): Chip | undefined =>
  typeof v === 'string' && Object.hasOwn(CHIP_KEYS, v) ? (v as Chip) : undefined

export const Route = createFileRoute('/$clusterId/$scopeId/fabric/data-model')({
  validateSearch: (search: Record<string, unknown>): FabricDataModelParams => ({
    stream: str(search.stream),
    q: str(search.q),
    chip: chipKey(search.chip),
  }),
  component: DataModelRoute,
})
